import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, UserPlus, Users, X } from 'lucide-react';
import type { AdminUser, ListAdminUsersData } from '../../generated/api/types.gen.js';
import {
  bulkUpdateUserStatusMutation,
  listAdminUsersOptions,
  listAdminUsersQueryKey,
} from '../../generated/api/@tanstack/react-query.gen.js';
import { useAuthStore } from '../../store/index.js';
import { Button, Callout, FilterPills, SearchBar, Toolbar, useToast } from '../../components/ui/index.js';
import { ListPage } from '../../components/page/ListPage.js';
import { Can } from '../../components/auth/Can.js';
import { PAGE_HELP } from '../../help/index.js';
import { InviteModal } from './InviteModal.js';
import { InvitationList } from './InvitationList.js';
import { UsersTable } from './UsersTable.js';

const PAGE_SIZE = 20;
const MANAGE_USERS = ['CanManageUsers', 'CanManageOrgMembers'];

type UserQuery = NonNullable<ListAdminUsersData['query']>;

const STATUS_PILLS = [
  { id: 'all', label: 'All' },
  { id: 'pending', label: 'Pending approval' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
];

const ROLE_PILLS = [
  { id: 'all', label: 'Any role' },
  { id: 'org_admin', label: 'Organization admin' },
  { id: 'trader', label: 'Trader' },
  { id: 'analyst', label: 'Analyst' },
  { id: 'viewer', label: 'Viewer' },
];

function toStatus(id: string): UserQuery['status'] {
  return id === 'pending' || id === 'approved' || id === 'rejected' ? id : undefined;
}

function toRole(id: string): UserQuery['role'] {
  return id === 'org_admin' || id === 'trader' || id === 'analyst' || id === 'viewer' ? id : undefined;
}

export const UsersPage: React.FC = () => {
  const help = PAGE_HELP.users;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const activeOrgId = useAuthStore((s) => s.user?.organizationId ?? null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusId, setStatusId] = useState('all');
  const [roleId, setRoleId] = useState('all');
  const [page, setPage] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [inviteOpen, setInviteOpen] = useState(false);

  // Wait for the person to stop typing before asking the server.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const query: UserQuery = {
    search: search || undefined,
    status: toStatus(statusId),
    role: toRole(roleId),
    orgId: activeOrgId ?? undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };

  const { data, isPending, isError, refetch } = useQuery({
    ...listAdminUsersOptions({ query }),
    placeholderData: keepPreviousData,
  });

  const users = data?.data ?? [];
  const total = data?.paging.total ?? 0;
  const pendingOnPage = users.filter((u) => u.status === 'pending').length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const decide = useMutation({
    ...bulkUpdateUserStatusMutation(),
    onSuccess: async (result) => {
      setSelectedIds(new Set());
      notify(`${result.updatedCount} ${result.updatedCount === 1 ? 'user' : 'users'} updated`);
      await queryClient.invalidateQueries({ queryKey: listAdminUsersQueryKey() });
    },
    onError: () => notify('Could not update the users', 'danger'),
  });
  const decideUsers = (ids: number[], status: 'approved' | 'rejected') => decide.mutate({ body: { ids, status } });

  const resetPaging = () => {
    setPage(0);
    setSelectedIds(new Set());
  };

  const toggleSelect = (id: number) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selectAll = () =>
    setSelectedIds((prev) =>
      users.length > 0 && users.every((u) => prev.has(u.id)) ? new Set() : new Set(users.map((u) => u.id)),
    );

  const inviteButton = (
    <Can policy="CanInviteUsers">
      <Button onClick={() => setInviteOpen(true)}>
        <UserPlus size={14} aria-hidden="true" />
        Invite user
      </Button>
    </Can>
  );

  let alert: React.ReactNode;
  if (isError) {
    alert = (
      <Callout
        tone="danger"
        icon={AlertTriangle}
        title="Could not load users"
        actions={
          <Button size="sm" variant="secondary" onClick={() => refetch()}>
            Retry
          </Button>
        }
      >
        Check your connection and try again.
      </Callout>
    );
  } else if (selectedIds.size > 0) {
    alert = (
      <Can policies={MANAGE_USERS} mode="any">
        <Callout
          tone="accent"
          icon={Users}
          title={`${selectedIds.size} ${selectedIds.size === 1 ? 'user' : 'users'} selected`}
          actions={
            <>
              <Button
                size="sm"
                variant="success"
                loading={decide.isPending}
                onClick={() => decideUsers([...selectedIds], 'approved')}
              >
                <Check size={14} aria-hidden="true" />
                Approve
              </Button>
              <Button
                size="sm"
                variant="secondary"
                loading={decide.isPending}
                onClick={() => decideUsers([...selectedIds], 'rejected')}
              >
                <X size={14} aria-hidden="true" />
                Reject
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>
                Clear selection
              </Button>
            </>
          }
        >
          Approve or reject everyone you selected in one step.
        </Callout>
      </Can>
    );
  } else if (pendingOnPage > 0 && statusId === 'all') {
    alert = (
      <Callout
        tone="warning"
        icon={AlertTriangle}
        title={`${pendingOnPage} ${pendingOnPage === 1 ? 'person is' : 'people are'} waiting for approval`}
        actions={
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setStatusId('pending');
              resetPaging();
            }}
          >
            Show pending
          </Button>
        }
      >
        They cannot use Conflui until their sign-up is approved.
      </Callout>
    );
  }

  return (
    <>
      <ListPage
        title="Users"
        subtitle="Manage accounts, roles and sign-up approvals."
        helpContent={help}
        actions={inviteButton}
        alert={alert}
        toolbar={
          <Toolbar
            search={<SearchBar value={searchInput} onChange={setSearchInput} placeholder="Search by name or email…" />}
            pills={
              <FilterPills
                aria-label="Status"
                options={STATUS_PILLS}
                value={statusId}
                onChange={(id) => {
                  setStatusId(id);
                  resetPaging();
                }}
              />
            }
            right={
              <FilterPills
                aria-label="Role"
                options={ROLE_PILLS}
                value={roleId}
                onChange={(id) => {
                  setRoleId(id);
                  resetPaging();
                }}
              />
            }
          />
        }
        footer={
          <>
            {total > PAGE_SIZE && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted">
                  Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total} users
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={page === 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted">
                    Page {page + 1} of {totalPages}
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
            {activeOrgId !== null && (
              <Can policy="CanInviteUsers">
                <section aria-labelledby="invitations-heading" className="space-y-3">
                  <h2 id="invitations-heading" className="text-lg font-semibold text-heading">
                    Invitations
                  </h2>
                  <InvitationList orgId={activeOrgId} />
                </section>
              </Can>
            )}
          </>
        }
      >
        {!isError && (
          <UsersTable
            users={users}
            loading={isPending}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onSelectAll={selectAll}
            onRowClick={(user: AdminUser) => navigate(`/organization/users/${user.id}`)}
            onDecide={(id, status) => decideUsers([id], status)}
            deciding={decide.isPending}
            emptyAction={inviteButton}
          />
        )}
      </ListPage>

      <InviteModal open={inviteOpen} onClose={() => setInviteOpen(false)} orgId={activeOrgId} />
    </>
  );
};
